import React, { useState, useEffect, memo } from 'react';
import CuiButton from '../CuiComponents/Button';
import CuiIcon from '../CuiComponents/Icon';
import FocusTrap from 'focus-trap-react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { navigate } from '../../redux/actions/CourseActions';
import { useTranslation } from 'react-i18next';
import './CourseIndex.scss';
import useXapi from '../../Hooks/useXapi';
import { actionsToHideModal } from '../../redux/actions/AppHeaderModalActions';
import { pseudolocalize } from '../Block/common/pseudolocalize/pseudolocalize';

const ContentContains = memo(({ types }) => {
  const { t } = useTranslation();
  const { filters } = useSelector(state => state.CourseIndexState);
  let filterTypes = filters.map(f => f.key);
  return (
    <span>
      {t('CourseIndex.contains')}{' '}
      <span className="italic">
        {types
          .filter(t => filterTypes.includes(t))
          .map(type =>
            t(`CourseIndex.${filters.find(f => f.key === type).key}`)
          )
          .join(', ')}
      </span>
    </span>
  );
});

const CourseIndex = () => {
  const { xApiEvent } = useXapi();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isPseudo,
    learningPathJson,
    filters,
    countsSet,
    activeFilters,
    filteredCourse
  } = useSelector(
    state => ({
      learningPathJson: state.CourseState.learningPathJson,
      filters: state.CourseIndexState.filters,
      countsSet: state.CourseIndexState.countsSet,
      activeFilters: state.CourseIndexState.activeFilters,
      filteredCourse: state.CourseIndexState.filteredCourse,
      isPseudo: state.LanguageState.isPseudo
    }),
    shallowEqual
  );

  const [showFilters, setShowFilters] = useState(true);
  const nothingToShow =
    filteredCourse && filteredCourse.hasPart.every(m => m.show === false);

  const bodyEle = document.getElementsByTagName('body')[0];

  const toggleFilter = type => {
    let fltrUpdts = JSON.parse(JSON.stringify(filters));
    if (type === 'all') {
      let active = !fltrUpdts[0].active;
      fltrUpdts = fltrUpdts.map(f => {
        f.active = active;
        return f;
      });
    } else {
      let filter = fltrUpdts.find(f => f.key === type);
      filter.active = !filter.active;
      fltrUpdts[0].active = fltrUpdts
        .filter(f => f.key !== 'all')
        .some(f => !f.active)
        ? false
        : true;
    }
    dispatch({
      type: 'UPDATE_FILTER_STATE',
      filters: fltrUpdts
    });
  };

  const toggleShowFilters = e => {
    e.preventDefault();
    setShowFilters(!showFilters);
  };

  useEffect(() => {
    if (!countsSet) {
      let fltrUpdts = JSON.parse(JSON.stringify(filters));
      learningPathJson.hasPart.forEach(m => {
        m.hasPart.forEach(b => {
          b.chunks.forEach(c => {
            c['content-types'].forEach(ct => {
              let filter = fltrUpdts.find(f => f.key === ct);
              if (filter) {
                filter.count = filter.count + 1;
              }
            });
          });
        });
      });
      dispatch({
        type: 'UPDATE_FILTER_STATE',
        filters: fltrUpdts
      });
    }
    if (!filteredCourse && activeFilters) {
      let lpUpdts = JSON.parse(JSON.stringify(learningPathJson));
      lpUpdts.hasPart.forEach(m => {
        m.show = false;
        return m.hasPart.forEach(b => {
          b.show = false;

          return b.chunks.forEach(c => {
            c.show = activeFilters.some(r => c['content-types'].includes(r));
            m.show = m.show ? true : c.show;
            b.show = b.show ? true : c.show;
            return c;
          });
        });
      });
      dispatch({ type: 'UPDATE_LP_STATE', filteredCourse: lpUpdts });
    }
    // prevent background scrolling while the bookmarks modal is visible
    bodyEle.style.cssText =
      'overflow: hidden; height: auto; padding-right: 15px;';
    return function cleanup() {
      bodyEle.style.cssText = '';
    };
  });

  return (
    <FocusTrap>
      <div className="course-index-outer">
        <div className="course-index-header-stuck">
          <div>
            <div className="icon-circle-wrap">
              <CuiIcon icon="list-view" size="medium" />
            </div>
            <h1>{t('CourseIndex.courseIndex')}</h1>
          </div>
          <CuiButton
            id="close-btn"
            icon="close"
            onClick={() => {
              xApiEvent('Course Index Modal Closed', window.location.href);
              dispatch(actionsToHideModal('HIDE_INDEX'));
            }}
          />
        </div>
        <div className={`filters-outer ${showFilters ? 'is-visible' : ''}`}>
          <div className="filters-inner">
            <div className="filters">
              {filters.map((filter, i) => (
                <div key={i} className="form-group form-group--inline">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={filter.active}
                      onChange={() => toggleFilter(filter.key)}
                    />
                    <span className="checkbox__input"></span>
                    <span className="checkbox__label">
                      {isPseudo
                        ? pseudolocalize(t(`CourseIndex.${filter.key}`))
                        : t(`CourseIndex.${filter.key}`)}
                      {filter.key !== 'all' ? (
                        <span className="count">({filter.count})</span>
                      ) : (
                        <span className="all-filters icon-arrow-right-tail" />
                      )}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
          <button
            className="toggle-filters"
            onClick={e => toggleShowFilters(e)}
          >
            {showFilters
              ? t('CourseIndex.hideFilters')
              : t('CourseIndex.showFilters')}
          </button>
        </div>
        <div className="modules-wrap">
          <ul
            className={`modules-container ${
              showFilters ? 'filters-visible' : ''
            }`}
          >
            {countsSet &&
              filteredCourse &&
              filteredCourse.hasPart.map(mod => {
                return (
                  mod.show && (
                    <li key={mod.li} className="module-item">
                      <div
                        className={`module-title ${
                          showFilters ? 'filters-visible' : ''
                        }`}
                      >
                        {mod.li} -{' '}
                        {isPseudo ? pseudolocalize(mod.name) : mod.name}
                      </div>
                      <ul className="blocks-container">
                        {mod.hasPart.map(
                          block =>
                            block.show && (
                              <li key={block.li} className="block-item">
                                <div className="block-title">
                                  <a
                                    href={`#${block.li}`}
                                    onClick={e => {
                                      e.preventDefault();
                                      dispatch(navigate(block));
                                    }}
                                  >
                                    {block.li} -{' '}
                                    {isPseudo
                                      ? pseudolocalize(block.name)
                                      : block.name}
                                  </a>
                                </div>
                                <ul className="chunks-container">
                                  {block.chunks.map(
                                    chunk =>
                                      chunk.show && (
                                        <li
                                          key={chunk.li}
                                          className="chunk-item"
                                        >
                                          <a
                                            href={`#${chunk.li}`}
                                            onClick={e => {
                                              e.preventDefault();
                                              dispatch(navigate(chunk));
                                            }}
                                          >
                                            {chunk.li} -{' '}
                                            {isPseudo
                                              ? pseudolocalize(chunk.name)
                                              : chunk.name}
                                          </a>
                                          <ContentContains
                                            types={chunk['content-types']}
                                          />
                                        </li>
                                      )
                                  )}
                                </ul>
                              </li>
                            )
                        )}
                      </ul>
                    </li>
                  )
                );
              })}
          </ul>
        </div>
        {nothingToShow && <h4>{t('CourseIndex.nothingToShow')}</h4>}
      </div>
    </FocusTrap>
  );
};
export default CourseIndex;
